import type { ReactNode } from 'react';
import clsx from 'clsx';

export type ErrorsProps = {
  errors?: string[];
  title?: string;
  variant?: 'inline' | 'banner' | 'list';
  icon?: ReactNode;
  onRetry?: () => void;
  retryLabel?: string;
  onDismiss?: () => void;
  className?: string;
};

const ErrorIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className}
  >
    <path
      fillRule="evenodd"
      d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5Zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
      clipRule="evenodd"
    />
  </svg>
);

const CloseIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className}
  >
    <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
  </svg>
);

const variantStyles = {
  inline: 'text-error dark:text-error-lighter',
  banner:
    'bg-error/10 dark:bg-error/20 text-error dark:text-error-lighter border border-error/20 rounded-lg p-4',
  list: 'bg-error/10 dark:bg-error/20 text-error dark:text-error-lighter border border-error/20 rounded-lg p-4',
};

function Errors({
  errors = [],
  title,
  variant = 'inline',
  icon,
  onRetry,
  retryLabel = 'Try again',
  onDismiss,
  className,
}: ErrorsProps) {
  if (errors.length === 0) return null;

  const IconComponent = icon || <ErrorIcon className="size-5 shrink-0" />;

  if (variant === 'inline') {
    return (
      <div className={clsx('flex items-start gap-2', variantStyles[variant], className)}>
        {IconComponent}
        <div className="text-sm">
          {title && <p className="font-medium">{title}</p>}
          {errors.length === 1 ? (
            <p>{errors[0]}</p>
          ) : (
            <ul className="list-disc list-inside space-y-0.5">
              {errors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }

  if (variant === 'banner') {
    return (
      <div
        className={clsx('flex items-start gap-3', variantStyles[variant], className)}
        role="alert"
      >
        {IconComponent}
        <div className="flex-1 min-w-0">
          {title && <p className="font-semibold">{title}</p>}
          {errors.length === 1 ? (
            <p className="text-sm">{errors[0]}</p>
          ) : (
            <ul className="list-disc list-inside text-sm space-y-0.5">
              {errors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          )}
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="mt-2 text-sm font-medium underline underline-offset-2 hover:no-underline"
            >
              {retryLabel}
            </button>
          )}
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="shrink-0 p-0.5 rounded hover:bg-error/10"
            aria-label="Dismiss"
          >
            <CloseIcon className="size-4" />
          </button>
        )}
      </div>
    );
  }

  // List variant
  return (
    <div className={clsx(variantStyles[variant], className)} role="alert">
      <div className="flex items-center gap-2 mb-2">
        {IconComponent}
        {title && <p className="font-semibold">{title}</p>}
      </div>
      <ul className="ml-7 list-disc list-inside text-sm space-y-1">
        {errors.map((error, idx) => (
          <li key={idx}>{error}</li>
        ))}
      </ul>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 ml-7 text-sm font-medium underline underline-offset-2 hover:no-underline"
        >
          {retryLabel}
        </button>
      )}
    </div>
  );
}

Errors.displayName = 'Errors';

export { Errors };
